import { useEffect, useReducer, useState } from "react";
import { CourseType, medicationsService, MedicationState, ProductStatus } from "../../services/medicationmanagement/medications";
import { IMedication } from "../../services/medicationmanagement/medications/models/Medication";
import { logger } from "../common/logger";

export type SelectableMedication = IMedication & {
    actioningInProgress: boolean;
    actioningFailed: string | null;
    isNewUpdatedMedication?:boolean;
}

export enum PharmacyErrors{
    MedicationLoadingError='MedicationLoadingError',
    MedicationConfirmationError='MedicationConfirmationError',
    MedicationActionError='MedicationActionError'
}
export interface ISelectablePharmacyMedications {
    loading: boolean;
    error: PharmacyErrors | null;
    confirmingInProgress?: boolean;
    data: {
        medicationId?: string;
        residentAuthoringSystem: string;
        residentAuthoringSystemForeignKey: string;
        meds: SelectableMedication[]
    }
}

export class GroupedResidentPharmacyMedications {
    private readonly regularMeds: Readonly<SelectableMedication[]>;
    private readonly prnMeds: Readonly<SelectableMedication[]>;
    private readonly shortCourseMeds: Readonly<SelectableMedication[]>;
    private readonly allMeds: Readonly<SelectableMedication[]>;
    private readonly ceasedConfirmedMeds: Readonly<SelectableMedication[]>;

    constructor(private meds: SelectableMedication[]) {
        var activeMeds = meds.filter(x => !(x.status === MedicationState.Ceased && x.confirmed))
                             .sort((a, b) => a.product.name.localeCompare(b.product.name));
        this.regularMeds = activeMeds.filter(x => x.courseType === CourseType.Regular);
        this.prnMeds = activeMeds.filter(x => x.courseType === CourseType.AsRequired);
        this.shortCourseMeds = activeMeds.filter(x => x.courseType === CourseType.ShortCourse);
        this.ceasedConfirmedMeds = meds.filter(x => x.status === MedicationState.Ceased && x.confirmed)
                                        .sort((a, b) => a.product.name.localeCompare(b.product.name));
        this.allMeds = meds;
    }

    get regular() {
        return {
            count: this.regularMeds?.length,
            data: this.regularMeds || []
        }
    }
    get prn() {
        return {
            count: this.prnMeds?.length,
            data: this.prnMeds || []
        }
    }
    get shortCourse() {
        return {
            count: this.shortCourseMeds?.length,
            data: this.shortCourseMeds || []
        };

    }
    get ceasedConfirmed() {
        return {
            count: this.ceasedConfirmedMeds?.length,
            data: this.ceasedConfirmedMeds || []
        };

    }
    all() {
        return this.allMeds;
    }
}

enum DataLoadingActions {
    StartFetching = 'StartFetching',
    FetchSucceeded = 'FetchSucceeded',
    FetchFailed = 'FetchFailed',
    StartConfirming = 'StartConfirming',
    ConfirmSucceeded = 'ConfirmSucceeded',
    ConfirmFailed = 'ConfirmFailed',
    NewRequestAvaiable = 'NewRequestAvaiable',
}

enum MedicationActions {
    StartChangeState = 'StartChangeState',
    ChangeStateSucceeded = 'ChangeStateSucceeded',
    ChangeStateFailed = 'ChangeStateFailed'
}

interface IMedicationAction {
    action: MedicationActions;
    payload: {
        authoringSystem: string;
        authoringSystemForeignKey: string,
        error?: any
    }
}

interface IPharmacyAction {
    action: DataLoadingActions;
    payload: any;
}



function medicationActionReducer(state: SelectableMedication[], action: IMedicationAction): SelectableMedication[] {

    let newState = state.map(x => {
        return { ...x };
    });
    let selectedItem = newState
        .find(x => x.authoringSystem === action.payload.authoringSystem && x.authoringSystemForeignKey === action.payload.authoringSystemForeignKey);

    switch (action.action) {
        case MedicationActions.StartChangeState:
            if (selectedItem) {
                selectedItem.actioningInProgress = true;
                selectedItem.actioningFailed = null;
                selectedItem.product = {
                    ...selectedItem.product,
                    action: selectedItem.product.action === ProductStatus.Actioned ? ProductStatus.Unactioned : ProductStatus.Actioned
                };
            }
            break;
        case MedicationActions.ChangeStateSucceeded:
            if (selectedItem) {
                selectedItem.actioningInProgress = false;
                selectedItem.actioningFailed = null;
            }
            break;
        case MedicationActions.ChangeStateFailed:
            if (selectedItem) {
                selectedItem.actioningInProgress = false;
                selectedItem.actioningFailed = action.payload.error;
                selectedItem.product = {
                    ...selectedItem.product,
                    action: selectedItem.product.action === ProductStatus.Actioned ? ProductStatus.Unactioned : ProductStatus.Actioned
                };
            }
            break;
    }
    return newState;
}

function loggerReducer(state: ISelectablePharmacyMedications, action: IPharmacyAction | IMedicationAction): ISelectablePharmacyMedications {
    logger.log(`Start ${action.action}`, state);
    let newState = reducer(state, action);
    logger.log(`End ${action.action}`, newState);
    return newState;
}

function reducer(state: ISelectablePharmacyMedications, action: IPharmacyAction | IMedicationAction): ISelectablePharmacyMedications {
    switch (action.action) {
        case DataLoadingActions.StartFetching:
            return {
                ...state,
                error: null,
                loading: true,
                data: {
                    meds: [],
                    ...action.payload
                }
            };
        case DataLoadingActions.FetchSucceeded:

            return {
                ...state,
                error: null,
                loading: false,
                data: {
                    ...state.data,
                    meds: (action?.payload?.medications || []).map((x: IMedication) => {
                        return {
                            ...x,
                            actioningInProgress: false,
                            actioningFailed: null
                        }
                    }),
                    medicationId: action?.payload?.id
                }
            };
        case DataLoadingActions.FetchFailed:
            return {
                ...state,
                error: PharmacyErrors.MedicationLoadingError,
                loading: false
            };
        case DataLoadingActions.StartConfirming:
            return {
                ...state,
                error: null,
                loading: false,
                confirmingInProgress: true
            };
        case DataLoadingActions.ConfirmSucceeded:
            return {
                ...state,
                error: null,
                loading: false,
                confirmingInProgress: false
            };
        case DataLoadingActions.ConfirmFailed:
            return {
                ...state,
                confirmingInProgress: false,
                error: PharmacyErrors.MedicationConfirmationError
            };
        case DataLoadingActions.NewRequestAvaiable:
            return {
                ...state,
                confirmingInProgress: false,
                error: null
            };
            case MedicationActions.ChangeStateFailed:
                return {
                    ...state,
                    error: PharmacyErrors.MedicationActionError,
                    loading: false,
                    data: {
                        ...state.data,
                        meds: medicationActionReducer(state.data.meds, action)
                    }
                }    
        default:
            return {
                ...state,
                error: null,
                loading: false,
                data: {
                    ...state.data,
                    meds: medicationActionReducer(state.data.meds, action)
                }
            }

    }
}

export const usePharmacyMedicationsLoader = (residentAuthoringSystem: string,
    residentAuthoringSystemForeignKey: string, pharmacyMedicationsOrderId?:string) => {
    let [state, dispatch] = useReducer(loggerReducer, {
        data: {
            meds: [],
            residentAuthoringSystem: '',
            residentAuthoringSystemForeignKey: ''
        },
        loading: true,
        error: null
    });

    
    const getMedications = async (residentAuthoringSystem: string,
                                  residentAuthoringSystemForeignKey: string, pharmacyMedicationsOrderId?:string)=>{

        dispatch({
            action: DataLoadingActions.StartFetching,
            payload: {
                residentAuthoringSystem: residentAuthoringSystem || '',
                residentAuthoringSystemForeignKey: residentAuthoringSystemForeignKey || ''
            }
        });
        try {
            let latestMedications = await medicationsService.getMedications(
                residentAuthoringSystem || '',
                residentAuthoringSystemForeignKey || ''
            );
            if (pharmacyMedicationsOrderId && latestMedications.id!==pharmacyMedicationsOrderId){
                let medications = await medicationsService.getMedications(
                    residentAuthoringSystem || '',
                    residentAuthoringSystemForeignKey || '',
                    pharmacyMedicationsOrderId
                );

                let scripts = new Map(medications.medications.map(x=>[x.scriptNumber,true]));

                let meds = latestMedications.medications.map(x=>{
                    return {
                        ...x,
                        isNewUpdatedMedication:!scripts.get(x.scriptNumber)
                    };
                });

                latestMedications.medications = [...meds];
            }
            dispatch({
                action: DataLoadingActions.FetchSucceeded,
                payload: latestMedications
            })
            return latestMedications;
        }catch(ex){
            dispatch({
                action: DataLoadingActions.FetchFailed,
                payload: ex
            });
        }
    };
    useEffect(() => {
        getMedications(
            residentAuthoringSystem || '',
            residentAuthoringSystemForeignKey || '',
            pharmacyMedicationsOrderId
        )}, [residentAuthoringSystem, residentAuthoringSystemForeignKey]);

    const toggleMedicationState = (medicationAuthSystem: string, medicationAuthSystemForeignKey: string) => {
        dispatch({
            action: MedicationActions.StartChangeState,
            payload: {
                authoringSystem: medicationAuthSystem,
                authoringSystemForeignKey: medicationAuthSystemForeignKey
            }
        });
        medicationsService
            .toggleActioned(
                state.data.residentAuthoringSystem
                , state.data.residentAuthoringSystemForeignKey
                , medicationAuthSystem, medicationAuthSystemForeignKey
                ,state.data.medicationId||''
            )
            .then(_ => {
                dispatch({
                    action: MedicationActions.ChangeStateSucceeded,
                    payload: {
                        authoringSystem: medicationAuthSystem,
                        authoringSystemForeignKey: medicationAuthSystemForeignKey
                    }
                });
            })
            .catch(err => {
                logger.error(err||'ChangeStateFailed');
                dispatch({
                    action: MedicationActions.ChangeStateFailed,
                    payload: {
                        authoringSystem: medicationAuthSystem,
                        authoringSystemForeignKey: medicationAuthSystemForeignKey,
                        error: err||'ChangeStateFailed'
                    }
                });
            });
    };

    return {
        loading: state.loading,
        error: state.error,
        data: state.data,
        confirmingInProgress: state.confirmingInProgress,
        getEncounterInfo: () => {
            return state.data.meds.map(x => {
                return {
                    authoringSystem: x.encounterAuthoringSystem,
                    authoringSystemForeignKey: x.encounterAuthoringSystemForeignKey
                }
            })[0];
        },
        reload:()=>{
            getMedications(
                residentAuthoringSystem || '',
                residentAuthoringSystemForeignKey || '',
                pharmacyMedicationsOrderId
            );
        },
        toggleMedicationState,
        selectAll: () => {
            let unSelected = state.data.meds.filter(x => x.product?.action !== ProductStatus.Actioned);
            unSelected.forEach(x => {
                toggleMedicationState(x.authoringSystem, x.authoringSystemForeignKey);
            });
        },
        unSelectAll: () => {
            let selected = state.data.meds.filter(x => x.product?.action === ProductStatus.Actioned);
            selected.forEach(x => {
                toggleMedicationState(x.authoringSystem, x.authoringSystemForeignKey);
            });
        },
        confirm: () => {
            dispatch({
                action: DataLoadingActions.StartConfirming,
                payload: null
            });

            return medicationsService.confirm({
                pharmacyMedicationsOrderId:state.data.medicationId||'',
                patient:{
                    authoringSystem:state.data.residentAuthoringSystem, 
                    authoringSystemForeignKey:state.data.residentAuthoringSystemForeignKey
                }
            })
                .then(_ => {
                    dispatch({
                        action: DataLoadingActions.ConfirmSucceeded,
                        payload: null
                    });
                    return true;
                })
                .catch(err => {
                    if (err.code==='Not_Latest'){
                        dispatch({
                            action: DataLoadingActions.NewRequestAvaiable,
                            payload: err
                        });
                        getMedications(state.data.residentAuthoringSystem,state.data.residentAuthoringSystemForeignKey,state.data.medicationId);
                        return Promise.reject();
                    }
                    dispatch({
                        action: DataLoadingActions.ConfirmFailed,
                        payload: err
                    });
                    return Promise.reject();
                });

        }
    };
}